import React, { useState, useEffect, useRef } from "react"
import { motion } from "framer-motion"
import { Heart, MapPin, MusicNotes, Camera, MagnifyingGlass, UserPlus, X, UserMinus, PencilSimple, Check, GearSix } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Separator } from "@/components/ui/separator"
import { toast, Toaster } from "sonner"
import guestListData from "./guestList.json"
import AdminPage from "./pages/AdminPage"
import { rsvpService, isSupabaseConfigured } from "@/lib/supabase"

type GuestGroup = {
  id: string
  names: string[]
  allowPlusOne: boolean
  maxGuests: number
}

const SITE_PASSWORD = "stella"
const WEDDING_DATE = new Date("2026-06-20T16:00:00")

// RSVP lock: when true, the public RSVP form is replaced with a closed notice.
// The owner can bypass by visiting the site with ?unlock=stellateam in the URL
// (the value must match the admin password), or by being signed in to /admin
// in the same browser session.
const RSVPS_LOCKED = true
const RSVP_UNLOCK_KEY = "stellateam"

function isRsvpUnlocked(): boolean {
  if (typeof window === "undefined") return false
  try {
    if (sessionStorage.getItem("admin-auth") === "true") return true
    if (sessionStorage.getItem("rsvp-unlocked") === "true") return true
    const params = new URLSearchParams(window.location.search)
    if (params.get("unlock") === RSVP_UNLOCK_KEY) {
      sessionStorage.setItem("rsvp-unlocked", "true")
      return true
    }
  } catch {
    // sessionStorage may be unavailable; treat as locked
  }
  return false
}

type Guest = {
  name: string
  meal: "salmon" | "beef" | "vegetarian" | "vegan" | ""
  allergies: string
  isAttending: boolean
  isPlusOne?: boolean
}

type RSVPData = {
  id: string
  guests: Guest[]
  notAttending: string[]  // Names of people from the group who are not attending
  songRequest: string
  submittedAt: string
}

const NAV_ITEMS = [
  { id: "home", label: "Home" },
  { id: "details", label: "Details" },
  { id: "gallery", label: "Gallery" },
  { id: "faq", label: "FAQ" },
  { id: "rsvp", label: "RSVP" },
]

function SideNav() {
  const [activeSection, setActiveSection] = useState("home")

  useEffect(() => {
    const handleScroll = () => {
      const sections = NAV_ITEMS.map(item => ({
        id: item.id,
        element: document.getElementById(item.id)
      })).filter(s => s.element)

      const scrollPosition = window.scrollY + window.innerHeight / 3

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i]
        if (section.element && section.element.offsetTop <= scrollPosition) {
          setActiveSection(section.id)
          break
        }
      }
    }

    window.addEventListener("scroll", handleScroll)
    handleScroll()
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: "smooth" })
    }
  }

  return (
    <motion.nav
      initial={{ x: -280, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ type: "spring", damping: 25, stiffness: 200, delay: 0.3 }}
      className="fixed top-0 left-0 h-full w-56 bg-card/80 backdrop-blur-md border-r border-border/30 z-40 flex flex-col justify-center px-6 hidden md:flex"
    >
      <div className="mb-10">
        <p className="text-2xl font-display font-light tracking-wide gold-shimmer">A & A</p>
        <p className="text-xs text-muted-foreground tracking-widest uppercase mt-1">June 20, 2026</p>
      </div>
      
      <div className="space-y-1">
        {NAV_ITEMS.map((item, index) => (
          <motion.button
            key={item.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 + index * 0.05 }}
            onClick={() => scrollToSection(item.id)}
            className={`w-full text-left py-3 px-4 flex items-center gap-3 transition-all duration-300 font-display text-base tracking-wide cursor-pointer ${
              activeSection === item.id 
                ? "text-sage" 
                : "text-foreground/60 hover:text-sage hover:bg-sage/5"
            }`}
          >
            <span className={`w-2 h-2 rounded-full transition-all duration-300 ${
              activeSection === item.id 
                ? "bg-sage scale-100" 
                : "bg-transparent scale-0"
            }`} />
            {item.label}
          </motion.button>
        ))}
      </div>
    </motion.nav>
  )
}

function Divider({ className = "" }: { className?: string }) {
  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div className="w-32 h-px bg-gradient-to-r from-transparent via-sage/60 to-transparent" />
    </div>
  )
}

function PasswordGate({ onUnlock }: { onUnlock: () => void }) {
  const [password, setPassword] = useState("")
  const [error, setError] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (password.toLowerCase() === SITE_PASSWORD.toLowerCase()) {
      sessionStorage.setItem("wedding-unlocked", "true")
      onUnlock()
    } else {
      setError(true)
      setTimeout(() => setError(false), 2000)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#8FA89A' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card className="border border-border/50 shadow-2xl bg-card/95 backdrop-blur-sm">
          <CardHeader className="text-center pb-4 pt-8">
            <Divider className="mb-6" />
            <CardTitle className="text-4xl font-display font-light tracking-wide gold-shimmer">
              A&A
            </CardTitle>
            <CardDescription className="text-base mt-4 text-muted-foreground font-light tracking-widest uppercase text-xs">
              Enter the password from your invitation
            </CardDescription>
          </CardHeader>
          <CardContent className="pb-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                id="password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`text-center text-base py-6 bg-muted/50 border-border/50 placeholder:text-muted-foreground/50 focus:border-gold ${error ? "border-destructive" : ""}`}
              />
              <Button type="submit" className="w-full py-6 text-sm tracking-widest uppercase bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                Enter
              </Button>
              {error && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-destructive text-center text-sm"
                >
                  Incorrect password
                </motion.p>
              )}
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}

function Countdown() {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 })

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date()
      const diff = WEDDING_DATE.getTime() - now.getTime()
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((diff / (1000 * 60)) % 60),
          seconds: Math.floor((diff / 1000) % 60),
        })
      }
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  const colors = ["text-wine", "text-sage", "text-gold", "text-plum"]

  return (
    <div className="flex justify-center gap-6 md:gap-10">
      {[
        { value: timeLeft.days, label: "Days" },
        { value: timeLeft.hours, label: "Hours" },
        { value: timeLeft.minutes, label: "Min" },
        { value: timeLeft.seconds, label: "Sec" },
      ].map((item, index) => (
        <div key={item.label} className="text-center">
          <div className="w-16 h-16 md:w-20 md:h-20 border border-gold/30 bg-card/50 backdrop-blur-sm flex items-center justify-center">
            <span className={`text-3xl md:text-4xl font-display font-light ${colors[index]}`}>
              {item.value.toString().padStart(2, "0")}
            </span>
          </div>
          <span className="text-[10px] md:text-xs text-muted-foreground mt-3 block tracking-[0.2em] uppercase">{item.label}</span>
        </div>
      ))}
    </div>
  )
}

function HomePage() {
  return (
    <motion.section
      id="home"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      className="min-h-screen flex flex-col items-center justify-center text-center relative overflow-hidden"
    >
      {/* Background Photo */}
      <div className="absolute inset-0 z-0">
        {/* Replace with your venue/couple photo */}
        {/* <img src="/hero-bg.jpg" alt="" className="w-full h-full object-cover" /> */}
        <div className="w-full h-full bg-gradient-to-b from-sage/40 via-sage/30 to-sage/50" />
        {/* Overlay for text readability */}
        <div className="absolute inset-0 bg-black/20" />
      </div>

      {/* Hero Content */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2, duration: 0.8 }}
        className="relative z-10 text-white px-4 max-w-4xl mx-auto"
      >
        <p className="text-white/80 mt-8 mb-6 tracking-[0.3em] uppercase text-xs font-light">Together with their families</p>
        
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-light tracking-wide mb-8">
          <span className="text-white">Alessia</span>
          <span className="text-2xl md:text-3xl lg:text-4xl italic text-white/80 mx-4">and</span>
          <span className="text-white">Anthony</span>
        </h1>

        {/* Couple Photo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="my-8"
        >
          <div className="max-w-md mx-auto rounded-lg overflow-hidden border-4 border-white/30 shadow-xl">
            <img src="/couple-photo.jpg" alt="Alessia & Anthony" className="w-full h-auto" />
          </div>
        </motion.div>
        
        <p className="text-lg md:text-xl text-white font-light tracking-wide mb-2">
          Request the honour of your presence
        </p>
        <p className="text-white/80 font-light mb-8">at the celebration of their marriage</p>

        <div className="flex items-center justify-center gap-4 text-white mb-4">
          <span className="text-2xl md:text-3xl font-display tracking-wide">June 20, 2026</span>
        </div>
        <div className="flex items-center justify-center gap-3 text-white/80 mb-10">
          <MapPin weight="light" className="w-4 h-4" />
          <span className="text-sm tracking-widest uppercase">Vieni Estates, Lincoln, On</span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mb-16"
        >
          <Button
            onClick={() => {
              const target = document.getElementById("rsvp")
              if (target) {
                const start = window.scrollY
                const end = target.offsetTop
                const duration = 3000
                const startTime = performance.now()
                
                const scroll = (currentTime: number) => {
                  const elapsed = currentTime - startTime
                  const progress = Math.min(elapsed / duration, 1)
                  const easeProgress = 1 - Math.pow(1 - progress, 3)
                  window.scrollTo(0, start + (end - start) * easeProgress)
                  if (progress < 1) requestAnimationFrame(scroll)
                }
                requestAnimationFrame(scroll)
              }
            }}
            className="px-8 py-6 text-sm tracking-widest uppercase bg-white/90 text-sage hover:bg-white font-medium"
          >
            <Heart weight="fill" className="w-4 h-4 mr-2" />
            RSVP Now
          </Button>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/60 text-xs tracking-widest uppercase"
      >
        Scroll below for more
      </motion.p>
    </motion.section>
  )
}

function DetailsSection() {
  return (
    <div className="bg-sage/10">
    <motion.section
      id="details"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: "-100px" }}
      className="max-w-4xl mx-auto px-4 py-16 md:py-24 relative z-10"
    >
      <div className="text-center mb-16">
        <Divider className="mb-6" />
        <h2 className="text-4xl md:text-5xl font-display font-light tracking-wide gold-shimmer mb-4">The Details</h2>
        <p className="text-muted-foreground font-light tracking-wide">Everything you need to know for the celebration</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Card className="border border-wine/20 bg-card/80 backdrop-blur-sm hover:border-wine/40 transition-colors">
            <CardHeader className="text-center pb-2">
              <div className="w-12 h-12 mx-auto mb-4 border border-wine/30 flex items-center justify-center rounded-full bg-wine/5">
                <Heart weight="light" className="w-6 h-6 text-wine" />
              </div>
              <CardTitle className="font-display text-2xl font-light tracking-wide">Ceremony</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-3">
              <p className="font-display text-xl text-wine">1:45 PM</p>
              <a href="https://maps.app.goo.gl/zWZc2USWs7sfM2nW6" target="_blank" rel="noopener noreferrer" className="text-foreground font-light hover:text-wine transition-colors underline underline-offset-2">Cathedral Basilica of Christ the King</a>
              <p className="text-muted-foreground text-sm font-light">714 King St W <br />Hamilton, ON L8P 1C7</p>
              <div className="pt-4 border-t border-border/30 mt-6">
                <p className="text-xs text-muted-foreground font-light tracking-wide">
                  Please arrive 5-10 minutes early.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          <Card className="border border-sage/20 bg-card/80 backdrop-blur-sm hover:border-sage/40 transition-colors">
            <CardHeader className="text-center pb-2">
              <div className="w-12 h-12 mx-auto mb-4 border border-sage/30 flex items-center justify-center rounded-full bg-sage/5">
                <span className="text-sage font-display text-lg">✦</span>
              </div>
              <CardTitle className="font-display text-2xl font-light tracking-wide">Reception</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-3">
              <p className="font-display text-xl text-sage">5:00 PM - 01:00 AM</p>
              <a href="https://maps.app.goo.gl/jC7MhxYb7QQM9Frj6" target="_blank" rel="noopener noreferrer" className="text-foreground font-light hover:text-sage transition-colors underline underline-offset-2">Vieni Estates</a>
              <p className="text-muted-foreground text-sm font-light">4553 Fly Rd <br /> Lincoln, ON L3J 1Z9</p>
              <div className="pt-4 border-t border-border/30 mt-6">
                <p className="text-xs text-muted-foreground font-light tracking-wide">
                  Cocktails, dinner, and dancing among the vineyards.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
        className="text-center py-8 px-6 border border-terracotta/20 bg-card/50 hover:border-terracotta/40 transition-colors"
      >
        <h3 className="font-display text-lg tracking-wide text-terracotta mb-4">Accommodations</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <div className="text-left p-3 bg-terracotta/5 rounded-lg border border-terracotta/10">
            <p className="font-display text-sm text-foreground">Quality Inn</p>
            <a href="https://maps.app.goo.gl/zzRJRRpnM6qWJELQ6" target="_blank" rel="noopener noreferrer" className="text-muted-foreground text-xs hover:text-terracotta transition-colors underline underline-offset-2">11 Windward Dr, Grimsby, ON L3M 4E9</a>
            <p className="text-sage text-xs mt-1.5 italic">Call and mention the wedding party for a special rate</p>
          </div>
          <div className="text-left p-3 bg-terracotta/5 rounded-lg border border-terracotta/10">
            <p className="font-display text-sm text-foreground">Ramada by Wyndham Jordan</p>
            <a href="https://maps.app.goo.gl/mcrMaCouPuqosX6v6" target="_blank" rel="noopener noreferrer" className="text-muted-foreground text-xs hover:text-terracotta transition-colors underline underline-offset-2">2793 Beacon Blvd, Jordan Station, ON L0R 1S0</a>
            <p className="text-sage text-xs mt-1.5">Block code: <span className="font-mono bg-sage/10 px-1.5 py-0.5 rounded">061925AMR</span></p>
          </div>
          <div className="text-left p-3 bg-terracotta/5 rounded-lg border border-terracotta/10">
            <p className="font-display text-sm text-foreground">Casablanca Hotel</p>
            <a href="https://maps.app.goo.gl/gDYGXp3dEJkYUBHd9" target="_blank" rel="noopener noreferrer" className="text-muted-foreground text-xs hover:text-terracotta transition-colors underline underline-offset-2">4 Windward Dr, Grimsby, ON L3M 4E8</a>
          </div>
        </div>
        <p className="text-muted-foreground text-xs mt-4">
          Nearby towns with more options: <span className="text-foreground/70 block sm:inline">Grimsby • St. Catharines • Lincoln • Beamsville</span>
        </p>
      </motion.div>
    </motion.section>
    </div>
  )
}

// Auto-import all gallery images sorted numerically
const galleryImages = import.meta.glob('/public/gallery/*.(jpg|jpeg|png|PNG|JPG|JPEG)', { eager: true, as: 'url' })
const galleryPhotos = Object.keys(galleryImages)
  .sort((a, b) => {
    // Extract numbers from filenames for numeric sorting
    const numA = parseInt(a.match(/(\d+)/)?.[1] || '0')
    const numB = parseInt(b.match(/(\d+)/)?.[1] || '0')
    return numA - numB
  })
  .map((path, index) => ({
    id: index + 1,
    src: path.replace('/public', ''),
  }))

function PhotosSection() {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [userScrolled, setUserScrolled] = useState(false)

  // Auto-scroll effect
  useEffect(() => {
    const scrollContainer = scrollRef.current
    if (!scrollContainer || userScrolled) return
    
    let animationId: number
    let scrollPos = 0
    const speed = 0.5 // pixels per frame
    
    const scroll = () => {
      scrollPos += speed
      if (scrollPos >= scrollContainer.scrollWidth - scrollContainer.clientWidth) {
        scrollPos = 0
      }
      scrollContainer.scrollLeft = scrollPos
      animationId = requestAnimationFrame(scroll)
    }
    
    animationId = requestAnimationFrame(scroll)
    
    // Pause on hover (desktop)
    const handleMouseEnter = () => cancelAnimationFrame(animationId)
    const handleMouseLeave = () => { 
      if (!userScrolled) animationId = requestAnimationFrame(scroll) 
    }
    
    // Stop permanently on touch (mobile) or click/scroll (desktop)
    const handleTouchStart = () => {
      cancelAnimationFrame(animationId)
      setUserScrolled(true)
    }
    
    const handleMouseDown = () => {
      cancelAnimationFrame(animationId)
      setUserScrolled(true)
    }
    
    // Detect manual scrolling (scrollbar drag, wheel, etc.)
    let lastScrollLeft = scrollContainer.scrollLeft
    const handleScroll = () => {
      // If the scroll position changed by more than the auto-scroll speed, user scrolled manually
      const diff = Math.abs(scrollContainer.scrollLeft - lastScrollLeft)
      if (diff > speed * 2) {
        cancelAnimationFrame(animationId)
        setUserScrolled(true)
      }
      lastScrollLeft = scrollContainer.scrollLeft
    }
    
    scrollContainer.addEventListener('mouseenter', handleMouseEnter)
    scrollContainer.addEventListener('mouseleave', handleMouseLeave)
    scrollContainer.addEventListener('touchstart', handleTouchStart)
    scrollContainer.addEventListener('mousedown', handleMouseDown)
    scrollContainer.addEventListener('scroll', handleScroll)
    
    return () => {
      cancelAnimationFrame(animationId)
      scrollContainer.removeEventListener('mouseenter', handleMouseEnter)
      scrollContainer.removeEventListener('mouseleave', handleMouseLeave)
      scrollContainer.removeEventListener('touchstart', handleTouchStart)
      scrollContainer.removeEventListener('mousedown', handleMouseDown)
      scrollContainer.removeEventListener('scroll', handleScroll)
    }
  }, [userScrolled])

  return (
    <div className="bg-cream">
      <motion.section
        id="gallery"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        className="py-16 md:py-24 relative z-10"
      >

        {/* Scrolling gallery */}
        <div 
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto px-4 pb-6 gallery-scroll"
        >
          {galleryPhotos.map((photo, index) => (
            <motion.div
              key={photo.id}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="flex-shrink-0 w-72 h-80 md:w-80 md:h-96 overflow-hidden border border-border/30 rounded-lg group hover:border-gold/30 transition-all duration-300"
            >
              <img src={photo.src} alt={`Photo ${photo.id}`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </motion.div>
          ))}
        </div>
      </motion.section>
    </div>
  )
}

function FAQSection() {
  const faqs = [
    { q: "Is there a registry?", a: "Your presence is the greatest gift we could ask for. If you wish to honor us further, a cash contribution to our future together would be greatly appreciated. No boxed gifts please." },
    { q: "Will there be transportation?", a: "Please note the church and reception are about 50 minutes apart. Transportation is not provided, so we recommend arranging a designated driver or Uber/taxi for the evening." },
    { q: "What should I wear?", a: <>The dress code is garden formal. We invite you to dress to the nines to help us celebrate our special day! Long dresses and skirts, suits and ties, and dressy separates are preferred.<br /><br />For outfit inspiration, check out our <a href="https://pin.it/67HIY4bqy" target="_blank" rel="noopener noreferrer" className="text-sage underline underline-offset-2 hover:text-sage/80">Pinterest board</a>.</> },
    { q: "Is there parking available?", a: "Yes! Complimentary parking will be provided both at the church and reception." },
    { q: "What time should I arrive?", a: "Please arrive 5-10 minutes before the ceremony at 1:45 PM." },
    { q: "Will there be vegetarian/vegan options?", a: "Absolutely! Please indicate your dietary preferences when you RSVP, as well as any allergies." },
  ]

  return (
    <div className="bg-sage/10">
    <motion.section
      id="faq"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: "-100px" }}
      className="max-w-3xl mx-auto px-4 py-16 md:py-24 relative z-10"
    >
      <div className="text-center mb-16">
        <Divider className="mb-6" />
        <h2 className="text-4xl md:text-5xl font-display font-light tracking-wide gold-shimmer mb-4">Questions</h2>
        <p className="text-muted-foreground font-light tracking-wide">Everything you might want to know</p>
      </div>

      <Accordion type="single" collapsible className="space-y-3">
        {faqs.map((faq, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.03 }}
          >
            <AccordionItem value={`faq-${index}`} className="border border-border/30 px-6 bg-card/50 data-[state=open]:border-sage/30 transition-colors">
              <AccordionTrigger className="text-left font-display font-normal text-lg hover:no-underline py-5 text-foreground/90 hover:text-sage transition-colors">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pb-5 font-light leading-relaxed">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          </motion.div>
        ))}
      </Accordion>
    </motion.section>
    </div>
  )
}

function RSVPSection() {
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState<GuestGroup[]>([])
  const [searchError, setSearchError] = useState("")
  const [verifiedGroup, setVerifiedGroup] = useState<GuestGroup | null>(null)
  const [guests, setGuests] = useState<Guest[]>([])
  const [notAttending, setNotAttending] = useState<string[]>([])
  const [songRequest, setSongRequest] = useState("")
  const [submitted, setSubmitted] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [existingRsvp, setExistingRsvp] = useState<RSVPData | null>(null)
  const [unlocked] = useState<boolean>(() => isRsvpUnlocked())

  if (RSVPS_LOCKED && !unlocked) {
    return (
      <motion.section
        id="rsvp"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        className="max-w-2xl mx-auto px-4 py-16 md:py-24 relative z-10"
      >
        <div className="text-center mb-12">
          <Divider className="mb-6" />
          <h2 className="text-4xl md:text-5xl font-display font-light tracking-wide gold-shimmer mb-4">RSVP</h2>
        </div>
        <Card className="border border-wine/20 bg-card/80 text-center">
          <CardHeader>
            <CardTitle className="font-display text-2xl font-light tracking-wide">
              RSVPs Are Closed
            </CardTitle>
            <CardDescription className="text-muted-foreground font-light pt-2">
              Thank you to everyone who responded. We can't wait to celebrate with you among the vines.
            </CardDescription>
          </CardHeader>
          <CardContent className="pb-8">
            <p className="text-sm text-muted-foreground font-light">
              If you need to change your response, please reach out to us directly.
            </p>
          </CardContent>
        </Card>
      </motion.section>
    )
  }

  const searchForName = () => {
    const query = searchQuery.trim().toLowerCase()
    if (query.length < 2) {
      setSearchError("Please enter at least 2 characters")
      setSearchResults([])
      return
    }

    // Match by full name, or full first name, or full last name
    const matches = guestListData.groups.filter((group) =>
      group.names.some((name) => {
        const fullName = name.toLowerCase()
        // Check if query matches the full name
        if (fullName === query) return true
        // Check if query matches any individual name part (first or last name)
        const nameParts = fullName.split(/[\s,]+/).filter(Boolean)
        return nameParts.some(part => part === query)
      })
    ) as GuestGroup[]

    if (matches.length === 0) {
      setSearchError("No guests found with that name. Please check the spelling or contact us.")
      setSearchResults([])
    } else {
      setSearchError("")
      setSearchResults(matches)
    }
  }

  const selectGroup = async (group: GuestGroup) => {
    setVerifiedGroup(group)
    setSearchResults([])
    setSearchQuery("")
    
    // Check Supabase for existing RSVP
    let existing: RSVPData | null = null
    
    if (!isSupabaseConfigured()) {
      toast.error("RSVP service is not available right now. Please try again later.", {
        duration: 8000,
      })
      return
    }

    {
      const dbRsvp = await rsvpService.getByGroupId(group.id)
      if (dbRsvp) {
        existing = {
          id: dbRsvp.group_id,
          guests: dbRsvp.guests.map(g => ({
            name: g.name,
            meal: g.meal,
            allergies: g.allergies,
            isAttending: g.is_attending,
            isPlusOne: g.is_plus_one
          })),
          notAttending: dbRsvp.not_attending,
          songRequest: dbRsvp.song_request,
          submittedAt: dbRsvp.submitted_at
        }
      }
    }
    
    if (existing) {
      // Load existing RSVP data, ensuring all fields are present
      setExistingRsvp(existing)
      const normalizedGuests = existing.guests.map(g => ({
        name: g.name,
        meal: g.meal,
        allergies: g.allergies || '',
        isAttending: g.isAttending ?? true,
        isPlusOne: g.isPlusOne ?? false
      }))
      setGuests(normalizedGuests)
      setNotAttending(existing.notAttending || [])
      setSongRequest(existing.songRequest || '')
      setSubmitted(true)
      setIsEditing(false)
    } else {
      // Initialize fresh form with all group members attending
      const initialGuests: Guest[] = group.names.map((name) => ({
        name,
        meal: "",
        allergies: "",
        isAttending: true,
        isPlusOne: false,
      }))
      setGuests(initialGuests)
      setNotAttending([])
      setExistingRsvp(null)
      setSubmitted(false)
      setIsEditing(false)
    }
  }

  const addPlusOne = () => {
    if (verifiedGroup && verifiedGroup.allowPlusOne && guests.length < verifiedGroup.maxGuests) {
      setGuests([...guests, { name: "", meal: "", allergies: "", isAttending: true, isPlusOne: true }])
    }
  }

  const removePlusOne = (index: number) => {
    const guest = guests[index]
    if (guest.isPlusOne) {
      setGuests(guests.filter((_, i) => i !== index))
    }
  }

  const markNotAttending = (guestName: string) => {
    // Move guest from attending to not attending
    setGuests(guests.filter(g => g.name !== guestName))
    setNotAttending([...notAttending, guestName])
  }

  const markAttending = (guestName: string) => {
    // Move guest from not attending back to attending
    setNotAttending(notAttending.filter(n => n !== guestName))
    setGuests([...guests, { name: guestName, meal: "", allergies: "", isAttending: true, isPlusOne: false }])
  }

  const updateGuest = (index: number, field: keyof Guest, value: string | boolean) => {
    setGuests(prev => prev.map((g, i) => 
      i === index ? { ...g, [field]: value } : g
    ))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!verifiedGroup) return

    // Only validate attending guests
    const attendingGuests = guests.filter(g => g.isAttending)
    
    if (attendingGuests.length === 0 && notAttending.length === 0) {
      toast.error("Please add at least one guest or mark someone as not attending")
      return
    }

    const hasEmptyName = attendingGuests.some(g => !g.name.trim())
    const hasEmptyMeal = attendingGuests.some(g => !g.meal)
    
    if (hasEmptyName) {
      toast.error("Please enter names for all attending guests")
      return
    }
    if (attendingGuests.length > 0 && hasEmptyMeal) {
      toast.error("Please select a meal for each attending guest")
      return
    }

    const newRsvp: RSVPData = {
      id: verifiedGroup.id,
      guests: attendingGuests,
      notAttending,
      songRequest,
      submittedAt: new Date().toISOString(),
    }

    // Save to Supabase
    if (!isSupabaseConfigured()) {
      toast.error("RSVP service is not available right now. Please try again later.", {
        duration: 8000,
      })
      return
    }

    try {
      await rsvpService.upsert({
        group_id: newRsvp.id,
        guests: newRsvp.guests.map(g => ({
          name: g.name,
          meal: g.meal,
          allergies: g.allergies,
          is_attending: g.isAttending,
          is_plus_one: g.isPlusOne || false
        })),
        not_attending: newRsvp.notAttending,
        song_request: newRsvp.songRequest,
        submitted_at: newRsvp.submittedAt
      })
    } catch (err) {
      console.error('Failed to save RSVP:', err)
      toast.error("Something went wrong saving your RSVP. Please try again.", {
        duration: 8000,
      })
      return
    }

    setExistingRsvp(newRsvp)
    setSubmitted(true)
    setIsEditing(false)
    toast.success(existingRsvp ? "RSVP updated!" : "Thank you for your RSVP!")
  }

  const startEditing = () => {
    setIsEditing(true)
    setSubmitted(false)
  }

  const resetForm = () => {
    setVerifiedGroup(null)
    setGuests([])
    setNotAttending([])
    setSongRequest("")
    setSubmitted(false)
    setIsEditing(false)
    setExistingRsvp(null)
  }

  if (submitted) {
    const guestsWithAllergies = guests.filter(g => g.allergies)
    
    return (
      <motion.section
        id="rsvp"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-2xl mx-auto px-4 py-16 md:py-24 text-center relative z-10"
      >
        <Divider className="mb-8" />
        <h2 className="text-4xl md:text-5xl font-display font-light tracking-wide gold-shimmer mb-4">
          {existingRsvp ? "Your RSVP" : "Thank You"}
        </h2>
        <p className="text-lg text-muted-foreground font-light mb-12">
          {guests.length > 0 
            ? "We can't wait to celebrate with you among the vines."
            : "We're sorry you can't make it. You'll be missed!"}
        </p>
        <Card className="border border-sage/20 bg-card/80 text-left">
          <CardContent className="p-8 space-y-6">
            {guests.length > 0 && (
              <div>
                <h3 className="text-wine text-xs tracking-widest uppercase mb-3">Attending</h3>
                {guests.map((guest, i) => (
                  <div key={i} className="mb-2">
                    <p className="text-foreground font-light">
                      {guest.name} <span className="text-muted-foreground">— {guest.meal.charAt(0).toUpperCase() + guest.meal.slice(1)}</span>
                    </p>
                    {guest.allergies && (
                      <p className="text-sm text-sage/80 ml-4">Dietary: {guest.allergies}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
            {notAttending.length > 0 && (
              <div>
                <h3 className="text-muted-foreground text-xs tracking-widest uppercase mb-3">Not Attending</h3>
                {notAttending.map((name, i) => (
                  <p key={i} className="text-muted-foreground font-light">{name}</p>
                ))}
              </div>
            )}
            {songRequest && (
              <div>
                <h3 className="text-gold text-xs tracking-widest uppercase mb-2">Song Request</h3>
                <p className="text-muted-foreground font-light">{songRequest}</p>
              </div>
            )}
          </CardContent>
        </Card>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
          <Button
            onClick={startEditing}
            variant="outline"
            className="tracking-widest uppercase text-xs"
          >
            <PencilSimple weight="bold" className="w-4 h-4 mr-2" />
            Edit Response
          </Button>
          <Button
            onClick={resetForm}
            variant="ghost"
            className="tracking-widest uppercase text-xs text-muted-foreground"
          >
            Search Another Name
          </Button>
        </div>
      </motion.section>
    )
  }

  // Name search screen
  if (!verifiedGroup) {
    return (
      <motion.section
        id="rsvp"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        className="max-w-2xl mx-auto px-4 py-16 md:py-24 relative z-10"
      >
        <div className="text-center mb-16">
          <Divider className="mb-6" />
          <h2 className="text-4xl md:text-5xl font-display font-light tracking-wide gold-shimmer mb-4">RSVP</h2>
          <p className="text-muted-foreground font-bold tracking-wide">Please respond by April 1, 2026</p>
        </div>

        <Card className="border border-wine/20 bg-card/80">
          <CardHeader className="text-center">
            <CardTitle className="font-display text-xl font-light tracking-wide">Find Your Invitation</CardTitle>
            <CardDescription className="text-muted-foreground font-light">
              Search for your full name to find your party
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-3">
              <Input
                placeholder="Enter your name..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setSearchError("")
                }}
                onKeyDown={(e) => e.key === "Enter" && searchForName()}
                className="bg-muted/30 border-border/50 focus:border-wine"
              />
              <Button
                onClick={searchForName}
                className="px-6 bg-wine text-primary-foreground hover:bg-wine/90"
              >
                <MagnifyingGlass weight="bold" className="w-5 h-5" />
              </Button>
            </div>
            {searchError && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-destructive text-center text-sm"
              >
                {searchError}
              </motion.p>
            )}
            {searchResults.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3 pt-4"
              >
                <p className="text-sm text-muted-foreground text-center">Select your party:</p>
                {searchResults.map((group) => (
                  <Button
                    key={group.id}
                    type="button"
                    variant="outline"
                    onClick={() => selectGroup(group)}
                    className="w-full h-auto py-4 border-sage/30 hover:border-sage hover:bg-sage/20 hover:text-foreground justify-start text-left"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-base whitespace-normal text-wrap">{group.names.join(", ")}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Party of {group.maxGuests}
                        {group.allowPlusOne && " (plus one allowed)"}
                      </p>
                    </div>
                  </Button>
                ))}
              </motion.div>
            )}
          </CardContent>
        </Card>
      </motion.section>
    )
  }

  // RSVP form for verified group
  return (
    <motion.section
      id="rsvp"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: "-100px" }}
      className="max-w-2xl mx-auto px-4 py-16 md:py-24 relative z-10"
    >
      <div className="text-center mb-16">
        <Divider className="mb-6" />
        <h2 className="text-4xl md:text-5xl font-display font-light tracking-wide gold-shimmer mb-4">RSVP</h2>
        <p className="text-muted-foreground font-bold tracking-wide">Please respond by April 1, 2026</p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="border border-wine/20 bg-card/80 mb-8">
          <CardHeader>
            <CardTitle className="font-display text-xl font-light tracking-wide flex items-center justify-between">
              <span>Your Party</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={resetForm}
                className="text-muted-foreground text-xs tracking-widest uppercase hover:text-white"
              >
                Change
              </Button>
            </CardTitle>
            <CardDescription className="text-muted-foreground font-light">
              {verifiedGroup.allowPlusOne && guests.length + notAttending.length < verifiedGroup.maxGuests
                ? `You may bring up to ${verifiedGroup.maxGuests - verifiedGroup.names.length} additional guest(s)`
                : `Party of ${verifiedGroup.maxGuests}`}
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Attending guests */}
        {guests.map((guest, index) => (
          <motion.div
            key={`attending-${index}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="border border-sage/20 bg-card/80 mb-6">
              <CardHeader>
                <CardTitle className="font-display text-xl font-light tracking-wide flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Check weight="bold" className="w-4 h-4 text-sage" />
                    {guest.isPlusOne ? "Additional Guest" : guest.name}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => guest.isPlusOne ? removePlusOne(index) : markNotAttending(guest.name)}
                    className="text-muted-foreground hover:text-destructive text-xs"
                  >
                    {guest.isPlusOne ? (
                      <X weight="bold" className="w-4 h-4" />
                    ) : (
                      <>
                        <UserMinus weight="bold" className="w-4 h-4 mr-1" />
                        Can't Attend
                      </>
                    )}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {guest.isPlusOne && (
                  <div>
                    <Label htmlFor={`guest-name-${index}`} className="text-xs tracking-widest uppercase text-muted-foreground">Full Name</Label>
                    <Input
                      id={`guest-name-${index}`}
                      placeholder="Enter guest's full name"
                      value={guest.name}
                      onChange={(e) => updateGuest(index, "name", e.target.value)}
                      className="mt-2 bg-muted/30 border-border/50 focus:border-sage"
                    />
                  </div>
                )}

                <div>
                  <Label className="text-xs tracking-widest uppercase text-muted-foreground mb-4 block">Meal Selection</Label>
                  <RadioGroup
                    value={guest.meal}
                    onValueChange={(value) => updateGuest(index, "meal", value)}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                  >
                    {[
                      { value: "salmon", label: "Salmon", subtext: "Salmon with herb maple glaze and chive beurre blanc", color: "wine" },
                      { value: "beef", label: "Beef", subtext: "New York Striploin with wild mushroom demi wine glaze", color: "terracotta" },
                      { value: "vegetarian", label: "Vegetarian", subtext: "Eggplant Parmigiana with ricotta cheese, spinach, pomodoro, pesto", color: "sage" },
                      { value: "vegan", label: "Vegan", subtext: "Grilled pepper stuffed with a quinoa vegetable medley", color: "gold" },
                    ].map(option => (
                      <Label
                        key={option.value}
                        htmlFor={`meal-${index}-${option.value}`}
                        className={`flex items-start gap-3 p-4 border cursor-pointer transition-all duration-300 ${
                          guest.meal === option.value 
                            ? `border-${option.color} bg-${option.color}/5` 
                            : "border-border/30 hover:border-sage/50"
                        }`}
                      >
                        <RadioGroupItem
                          value={option.value}
                          id={`meal-${index}-${option.value}`}
                          className="mt-0.5 border-sage/50 text-sage"
                        />
                        <div className="flex flex-col">
                          <span className="font-display text-base">{option.label}</span>
                          <span className="text-xs text-muted-foreground font-light">{option.subtext}</span>
                        </div>
                      </Label>
                    ))}
                  </RadioGroup>
                </div>

                <div>
                  <Label htmlFor={`allergies-${index}`} className="text-xs tracking-widest uppercase text-muted-foreground">
                    Allergies / Dietary Restrictions
                  </Label>
                  <Input
                    id={`allergies-${index}`}
                    placeholder="e.g., nut allergy, gluten-free..."
                    value={guest.allergies}
                    onChange={(e) => updateGuest(index, "allergies", e.target.value)}
                    className="mt-2 bg-muted/30 border-border/50 focus:border-gold"
                  />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}

        {/* Not attending guests */}
        {notAttending.map((name, index) => (
          <motion.div
            key={`not-attending-${index}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="border border-border/30 bg-muted/30 mb-6 opacity-60">
              <CardContent className="p-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <X weight="bold" className="w-4 h-4 text-muted-foreground" />
                  <span className="font-display text-lg font-light text-muted-foreground">{name}</span>
                  <span className="text-xs text-muted-foreground tracking-widest uppercase">Not Attending</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => markAttending(name)}
                  className="text-sage hover:text-sage/80 text-xs"
                >
                  <UserPlus weight="bold" className="w-4 h-4 mr-1" />
                  Add Back
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ))}

        {/* Add plus one button */}
        {verifiedGroup.allowPlusOne && guests.length + notAttending.length < verifiedGroup.maxGuests && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Button
              type="button"
              variant="outline"
              onClick={addPlusOne}
              className="w-full mb-6 py-6 border-dashed border-sage/30 text-sage hover:bg-sage/5 hover:border-sage tracking-widest uppercase text-xs"
            >
              <UserPlus weight="light" className="w-5 h-5 mr-2" />
              Add Guest
            </Button>
          </motion.div>
        )}

        <Card className="border border-gold/20 bg-card/80 mb-8">
          <CardContent className="p-6">
            <div>
              <Label htmlFor="song-request" className="text-xs tracking-widest uppercase text-muted-foreground flex items-center gap-2">
                <MusicNotes weight="light" className="w-4 h-4 text-plum" />
                Song Request
              </Label>
              <Input
                id="song-request"
                placeholder="What song will get you on the dance floor?"
                value={songRequest}
                onChange={(e) => setSongRequest(e.target.value)}
                className="mt-2 bg-muted/30 border-border/50 focus:border-plum text-sm placeholder:text-xs"
              />
            </div>
          </CardContent>
        </Card>

        <Button
          type="submit"
          size="lg"
          className="w-full py-7 text-sm tracking-[0.2em] uppercase bg-wine text-primary-foreground hover:bg-wine/90 font-medium transition-all duration-300"
        >
          {existingRsvp ? "Update RSVP" : "Submit RSVP"}
        </Button>
      </form>
    </motion.section>
  )
}

function WeddingSite() {
  return (
    <div className="min-h-screen bg-vineyard">
      <SideNav />
      <div className="md:ml-56">
        <HomePage />
        <DetailsSection />
        <PhotosSection />
        <FAQSection />
        <div className="bg-cream">
          <RSVPSection />
        </div>

        <footer className="py-12 text-center border-t border-border/30 bg-sage/10">
          <Divider className="mb-4" />
          <p className="text-muted-foreground text-xs tracking-[0.2em] uppercase">Alessia & Anthony</p>
          <p className="text-muted-foreground/50 text-xs mt-2 tracking-wide">June 20, 2026 · Vieni Estates</p>
          <a 
            href="/admin" 
            className="inline-flex items-center gap-1 text-muted-foreground/30 text-xs mt-4 hover:text-muted-foreground transition-colors"
          >
            <GearSix className="w-3 h-3" />
          </a>
        </footer>
      </div>

      <Toaster position="top-center" richColors toastOptions={{
        style: {
          background: 'oklch(0.99 0.005 85)',
          border: '1px solid oklch(0.88 0.02 85)',
          color: 'oklch(0.25 0.02 300)',
        },
      }} />
    </div>
  )
}

function App() {
  const [unlocked, setUnlocked] = useState(false)
  const [currentPath, setCurrentPath] = useState(window.location.pathname)

  useEffect(() => {
    if (sessionStorage.getItem("wedding-unlocked") === "true") {
      setUnlocked(true)
    }

    // Handle browser back/forward
    const handlePopState = () => {
      setCurrentPath(window.location.pathname)
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  // Admin page route
  if (currentPath === '/admin') {
    return <AdminPage />
  }

  if (!unlocked) {
    return <PasswordGate onUnlock={() => setUnlocked(true)} />
  }

  return <WeddingSite />
}

export default App
